import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock3 } from 'lucide-react';

export const otpMoney = value => value == null || !Number.isFinite(Number(value)) ? 'Sin datos'
  : `$${Number(value).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const age = (stamp, now) => stamp && Number.isFinite(Date.parse(stamp)) ? Math.max(0, now - Date.parse(stamp)) : Infinity;
const ago = (stamp, now) => {
  const elapsed = age(stamp, now);
  if (!Number.isFinite(elapsed)) return 'sin fecha de consulta';
  const seconds = Math.floor(elapsed / 1000);
  if (seconds < 60) return `hace ${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;
  return new Date(stamp).toLocaleString('es-AR');
};

export const OtpArqueoOperacion = ({ data, cvuId, bankState, busy, error }) => {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => { if (document.visibilityState === 'visible') setNow(Date.now()); }, 10000);
    return () => clearInterval(timer);
  }, []);
  const live = Object.values(bankState?.accounts || {}).find(a => Number(a.cvu_id) === cvuId);
  const balance = live ? live.balance : data.saldo_real;
  const bankAt = live ? bankState.checked_at : data.cuenta?.real_balance_updated_at;
  const validBalance = balance != null && typeof balance !== 'boolean' && Number.isFinite(Number(balance));
  const validLedger = data.neto_total != null && typeof data.neto_total !== 'boolean' && Number.isFinite(Number(data.neto_total));
  const amountCents = validLedger ? Math.round(Number(data.neto_total) * 100) : null;
  const hasLedgerCents = Object.prototype.hasOwnProperty.call(data, 'neto_total_centavos');
  const invalidLedgerPrecision = validLedger && (!Number.isSafeInteger(amountCents) || Number(data.neto_total) !== amountCents / 100
    || (hasLedgerCents && (!Number.isSafeInteger(data.neto_total_centavos) || data.neto_total_centavos !== amountCents)));
  const ledgerCents = invalidLedgerPrecision || !Number.isSafeInteger(amountCents) ? null : amountCents;
  const bankCents = validBalance ? Math.round(Number(balance) * 100) : null;
  const invalidBankPrecision = validBalance && (!Number.isSafeInteger(bankCents) || Number(balance) !== bankCents / 100);
  const differenceCents = !invalidBankPrecision && Number.isSafeInteger(ledgerCents) && Number.isSafeInteger(bankCents) ? bankCents - ledgerCents : null;
  const difference = differenceCents == null ? null : differenceCents / 100;
  const precisionReviews = Number(data.precision_reviews || 0);
  const unknown = Object.entries(data.tipos_no_clasificados || {});
  const outdated = !!error || age(bankAt, now) > 90000 || age(data.calculado_at, now) > 90000;
  const good = validLedger && validBalance && !busy && !outdated && !unknown.length && !precisionReviews && differenceCents === 0;
  const status = busy ? 'Actualizando…' : unknown.length ? 'Saldo parcial'
    : !validBalance ? 'Banco sin datos' : !validLedger ? 'Libros sin datos' : precisionReviews || invalidLedgerPrecision || invalidBankPrecision ? 'Precisión por revisar'
    : outdated ? 'Datos desactualizados' : good ? 'Cuadra' : 'Diferencia por revisar';
  return (
    <div data-testid="otp-ops-arqueo" className={`space-y-2 rounded-lg p-3 border text-xs ${good
      ? 'border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20'
      : 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span data-testid="otp-ops-status" role="status" className="inline-flex items-center gap-1.5 font-medium">
          {good ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}{status}
        </span>
        <span data-testid="otp-ops-auto" className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400"><Clock3 className="w-3 h-3" />Automático · 30 s</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3">
        <div data-testid="otp-ops-neto-total">Suma de libros y gastos<strong className="block mt-1 tabular-nums">{otpMoney(data.neto_total)}</strong></div>
        <div data-testid="otp-ops-saldo-real">Saldo bancario<strong className="block mt-1 tabular-nums">{otpMoney(balance)}</strong></div>
        <div data-testid="otp-ops-diferencia">Diferencia<strong className="block mt-1 tabular-nums">{otpMoney(difference)}</strong></div>
        <div data-testid="otp-ops-movimientos">Movimientos<strong className="block mt-1 tabular-nums">{data.movimientos}</strong></div>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-gray-600 dark:text-gray-400">
        <span data-testid="otp-ops-ledger-updated" title={data.calculado_at || ''}>Libros calculados {ago(data.calculado_at, now)}</span>
        <span data-testid="otp-ops-bank-updated" title={bankAt || ''}>Banco consultado {ago(bankAt, now)}{!live ? ' · lectura guardada' : ''}</span>
      </div>
      {unknown.length > 0 && <p role="alert" data-testid="otp-ops-unknown-types" className="text-amber-800 dark:text-amber-300">
        Pendientes de clasificación: {unknown.map(([type, count]) => `${type} (${count})`).join(', ')}. No incluidos en el saldo calculado.
      </p>}
      {precisionReviews > 0 && <p role="alert" data-testid="otp-ops-precision-reviews" className="text-amber-800 dark:text-amber-300">
        {precisionReviews} movimientos con precisión del neto pendiente de revisión.
      </p>}
    </div>
  );
};